import { API_URL } from '../config';
import { Team, Tournament } from '../models/Tournament';
import { transformDate } from '../utils/Dates';
import { buildHeaders } from './headers';

export const fetchAllTournaments = async (apiKey: string, tenantId: number): Promise<Tournament[]> => {
  const response = await fetch(`${API_URL}/api/Tournament/${tenantId}/tournaments`, {
    method: 'GET',
    headers: buildHeaders(apiKey),
  });
  if (response.ok) {
    const data = await response.json();
    return data;
  } else {
    return Promise.reject(response);
  }
};

export const fetchTournament = async (apiKey: string, id: string): Promise<Tournament> => {
  const response = await fetch(`${API_URL}/api/tournament/${id}`, {
    method: 'GET',
    headers: buildHeaders(apiKey),
  });
  if (response.ok) {
    const data = await response.json();
    return data;
  } else {
    return Promise.reject(response);
  }
};

export const postTournament = async (apiKey: string, tournament: Tournament): Promise<Tournament> => {
  if (tournament.startDate) {
    tournament.startDate = transformDate(tournament.startDate);
  }
  const tournamentDto = tournament;
  const response = await fetch(`${API_URL}/api/tournament`, {
    method: 'POST',
    body: JSON.stringify(tournamentDto),
    headers: buildHeaders(apiKey),
  });
  if (response.ok) {
    const data = await response.json();
    return data;
  } else {
    return Promise.reject(response);
  }
};

export const deleteTournament = async (apiKey: string, tournamentId: number) => {
  const response = await fetch(`${API_URL}/api/tournament/${tournamentId}`, {
    method: 'DELETE',
    headers: buildHeaders(apiKey),
  });
  if (response.ok) {
    return response.ok;
  } else {
    return Promise.reject(response);
  }
};

export enum TrophyType {
  Shooter = 1,
  Goalkeeper = 2,
  MVP = 3,
}

export interface IndividualTrophy {
  type: TrophyType;
  player: string;
}

export const getChampions = async (tournamentId: number): Promise<{ teams: Team[]; individualTrophies: IndividualTrophy[] }> => {
  try {
    const response = await fetch(`${API_URL}/api/tournament/${tournamentId}/champions`);
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      return Promise.reject(response);
    }
  } catch (error) {
    return Promise.reject();
  }
};

export interface GroupTable {
  groupId: number;
  groupTableRows: GroupTableRow[];
}

export interface GroupTableRow {
  teamRanking: number;
  teamLogo?: string;
  teamId: number;
  teamName?: string;
  teamPoints: number;
  matchesCount: number;
  teamLossGoals: number;
  teamWinGoals: number;
  teamGoalsBalance: number;
}

export const getTournamentResult = async (
  tournamentId: string,
): Promise<{ _tournament: Tournament; groupTables: GroupTable[] }> => {
  try {
    const response = await fetch(`${API_URL}/api/tournament/${tournamentId}/table`);
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      return Promise.reject(response);
    }
  } catch (error) {
    return Promise.reject();
  }
};

export const endMatch = async (apiKey: string, tournamentId: number, matchId: number) => {
  try {
    const response = await fetch(`${API_URL}/api/tournament/${tournamentId}/matches/${matchId}/finish`, {
      method: 'POST',
      headers: buildHeaders(apiKey),
    });
    if (response.ok) {
      return response.ok;
    } else {
      return Promise.reject(response);
    }
  } catch (error) {
    return Promise.reject();
  }
};

export const uploadOfferImage = async (apiKey: string, tournamentId: string, file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_URL}/api/tournament/${tournamentId}/upload-offer-image`, {
    method: 'POST',
    headers: {
      'X-Api-Key': apiKey,
    },
    body: formData,
  });

  if (response.ok) {
    const data = await response.json();
    return data.offerUrl;
  } else {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Error uploading file.');
  }
};

export const deleteOfferImage = async (apiKey: string, tournamentId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/api/tournament/${tournamentId}/delete-offer-image`, {
    method: 'DELETE',
    headers: {
      'X-Api-Key': apiKey,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete file');
  }
};
